import React, { useState } from 'react';
import './PhaseMatchCard.css';

const TEAM_COLORS = {
  "Adelaide": ["#003366", "#FFCC00"],
  "Brisbane Lions": ["#6C1D45", "#FFCC00"],
  "Carlton": ["#0E1E2D", "#FFFFFF"],
  "Collingwood": ["#000000", "#FFFFFF"],
  "Essendon": ["#CC2031", "#000000"],
  "Fremantle": ["#2A1A54", "#FFFFFF"],
  "Geelong": ["#002B5C", "#FFFFFF"],
  "Gold Coast": ["#FF8C00", "#E4002B"],
  "Greater Western Sydney": ["#FF5E00", "#7B7F7F"],
  "Hawthorn": ["#4D2004", "#FBBF15"],
  "Melbourne": ["#E4002B", "#001C56"],
  "North Melbourne": ["#003D73", "#FFFFFF"],
  "Port Adelaide": ["#003B5C", "#FFFFFF"],
  "Richmond": ["#FFC72C", "#000000"],
  "St Kilda": ["#E4002B", "#FFFFFF"],
  "Sydney": ["#E4002B", "#FFFFFF"],
  "West Coast": ["#003087", "#FFCD00"],
  "Western Bulldogs": ["#1D428A", "#E30613"],
};

function teamColor(name) {
  return (TEAM_COLORS[name] || ["#666", "#aaa"])[0];
}

function ConfidencePill({ level }) {
  const map = { high: '#2e7d32', medium: '#f57c00', low: '#c62828' };
  return (
    <span className="phase-confidence-pill" style={{ background: map[level] || '#555' }}>
      {level}
    </span>
  );
}

const HOME_BAR_COLOR = '#3b82f6'; // fallback only
const AWAY_BAR_COLOR = '#f97316'; // fallback only

function PhaseBar({ phase, homeTeam, awayTeam }) {
  const maxAbs = 2.0;
  const clamp = v => Math.max(-maxAbs, Math.min(maxAbs, v));
  const val = clamp(phase.score);

  const homeColor = teamColor(homeTeam);
  const awayColor = teamColor(awayTeam);

  // Dominant side: full opacity team colour
  // Receding side: 12% opacity tint — nearly invisible, maximum contrast
  const homeOpacity = val >= 0 ? 1 : 0.12;
  const awayOpacity = val < 0  ? 1 : 0.12;

  const homeHalf = val >= 0 ? (val / maxAbs) * 50 : 0;
  const awayHalf = val < 0  ? (Math.abs(val) / maxAbs) * 50 : 0;

  return (
    <div className="phase-bar-row">
      <span className="phase-bar-label">{phase.label}</span>
      <div className="phase-bar-track">
        {/* Left half — home fills rightward from centre */}
        <div className="phase-bar-half phase-bar-left">
          <div
            className="phase-bar-fill"
            style={{
              width: `${homeHalf * 2}%`,
              background: homeColor,
              opacity: homeOpacity,
            }}
          />
        </div>
        {/* White centre divider */}
        <div className="phase-bar-centre" />
        {/* Right half — away fills leftward from centre */}
        <div className="phase-bar-half phase-bar-right">
          <div
            className="phase-bar-fill"
            style={{
              width: `${awayHalf * 2}%`,
              background: awayColor,
              opacity: awayOpacity,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function PlayerPanel({ players, teamName, side }) {
  if (!players || players.length === 0) return null;
  const phases = ['accumulation', 'territory', 'chance', 'conversion'];
  const phaseShort = { accumulation: 'Acc', territory: 'Ter', chance: 'Cha', conversion: 'Con' };
  const color = teamColor(teamName);

  // Max total impact across all players — used to scale the total bar
  const maxTotal = Math.max(...players.map(p => p.total_impact), 0.001);

  // Max per-phase impact across all players for each phase — scales each phase column independently
  const maxByPhase = {};
  for (const ph of phases) {
    maxByPhase[ph] = Math.max(...players.map(p => Math.abs(p.phase_impact[ph] || 0)), 0.001);
  }

  return (
    <div className={`player-panel player-panel-${side}`}>
      <div className="player-panel-title" style={{ color }}>
        {teamName} — influence share
      </div>
      <table className="player-table">
        <thead>
          <tr>
            <th>Player</th>
            {phases.map(p => <th key={p}>{phaseShort[p]}</th>)}
            <th>Overall</th>
          </tr>
        </thead>
        <tbody>
          {players.map((p, i) => (
            <tr key={i}>
              <td className="player-name-cell">{p.name}</td>
              {phases.map(ph => {
                const val = Math.abs(p.phase_impact[ph] || 0);
                const pct = (val / maxByPhase[ph]) * 100;
                return (
                  <td key={ph} className="player-phase-cell">
                    <div className="player-bar-wrap">
                      <div
                        className="player-bar-fill"
                        style={{ width: `${pct}%`, background: color }}
                      />
                    </div>
                  </td>
                );
              })}
              <td className="player-total-cell">
                <div className="player-bar-wrap player-bar-wrap-total">
                  <div
                    className="player-bar-fill"
                    style={{ width: `${(p.total_impact / maxTotal) * 100}%`, background: color }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PhaseMatchCard({ match }) {
  const [expanded, setExpanded] = useState(false);
  const { home_team, away_team, venue, prediction, result } = match;
  const { home_win_prob, away_win_prob, predicted_winner, confidence,
          margin_pts, sigma_pts, phase_scores, players } = prediction;

  const marginSign = margin_pts >= 0 ? `+${margin_pts.toFixed(0)}` : margin_pts.toFixed(0);
  const marginLabel = margin_pts >= 0
    ? `${home_team.split(' ')[0]} by ~${Math.abs(margin_pts).toFixed(0)} ± ${sigma_pts.toFixed(0)}`
    : `${away_team.split(' ')[0]} by ~${Math.abs(margin_pts).toFixed(0)} ± ${sigma_pts.toFixed(0)}`;

  return (
    <div className={`phase-match-card ${expanded ? 'expanded' : ''}`}>
      {/* Header row */}
      <div className="phase-card-header" onClick={() => setExpanded(e => !e)}>
        <div className="phase-teams">
          <span className="phase-team" style={{ color: teamColor(home_team) }}>
            {home_team}
          </span>
          <span className="phase-vs">vs</span>
          <span className="phase-team" style={{ color: teamColor(away_team) }}>
            {away_team}
          </span>
        </div>

        <div className="phase-prediction-summary">
          <div className="phase-winner">
            <strong>{predicted_winner}</strong>
            <ConfidencePill level={confidence} />
          </div>
          <div className="phase-probs">
            {(home_win_prob * 100).toFixed(0)}% / {(away_win_prob * 100).toFixed(0)}%
          </div>
          <div className="phase-margin-est">{marginLabel}</div>
        </div>

        <div className="phase-venue">{venue}</div>

        {result && (
          <div className={`phase-result ${result.correct ? 'correct' : 'incorrect'}`}>
            {result.correct ? '✓' : '✗'} {result.home_score} — {result.away_score}
          </div>
        )}

        <button className="phase-expand-btn">{expanded ? '▲' : '▼'}</button>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="phase-card-detail">
          <div className="phase-bars-section">
            <div className="phase-bars-header">
              <span style={{ color: teamColor(home_team) }}>{home_team}</span>
              <span className="phase-bars-title">Phase Breakdown</span>
              <span style={{ color: teamColor(away_team) }}>{away_team}</span>
            </div>
            {(phase_scores || []).map(ps => (
              <PhaseBar key={ps.phase} phase={ps} homeTeam={home_team} awayTeam={away_team} />
            ))}
          </div>

          <div className="phase-players-section">
            <PlayerPanel players={players?.home} teamName={home_team} side="home" />
            <PlayerPanel players={players?.away} teamName={away_team} side="away" />
          </div>

        </div>
      )}
    </div>
  );
}

export default PhaseMatchCard;
